// DOM wiring and rendering for the CRF tool. Pure logic lives in the imported
// modules (schema / util / stats / storage); this file only reads
// and writes the DOM and orchestrates the modules.

import "./style.css";

import {
  hasValue,
  num,
  round,
  sumIfAny,
  uniqueValues,
  fmt,
  fmtP,
  dateStamp,
  escapeHtml,
  escapeAttr,
  normalizeVessel,
  type Row,
} from "./util";
import {
  DB_COLUMNS,
  SECTIONS,
  FIELD_MAP,
  ANALYSIS_COLUMNS,
  ANALYSIS_SCENARIOS,
  GATING_RULES,
  getFieldMeta,
  naCodeFor,
  rowValue,
  sanitizeForAnalysis,
  strategyLabel,
  parseChoices,
  type FieldDef,
  type Scenario,
} from "./schema";
import {
  summary,
  inferVariableType,
  buildMethodRecommendation,
  buildDesignMatrix,
  ols,
  logisticScores,
  nearestMatches,
  psmOutcomeEffect,
  maxAbsSmd,
  fisherExact2x2,
  studentTCdf,
  pairwiseT,
  pairwiseFisher,
  type MethodRecommendation,
} from "./stats";
import {
  DRAFT_KEY,
  rowsToCsv,
  rowsToCleanCsv,
  rowsToJsonPayload,
  parseImportedRows,
} from "./storage";
import { codebookToCsv } from "./codebook";
import { CHANGELOG, CURRENT_VERSION } from "./changelog";
import { createRepository } from "./backend";

let repo: ReturnType<typeof createRepository>;
try {
  repo = createRepository();
} catch (error) {
  const el = document.getElementById("configError");
  const msg = document.getElementById("configErrorMessage");
  if (el) {
    if (msg) msg.textContent = `URL сервера базы данных не задан или некорректен. ${error instanceof Error ? error.message : String(error)}`;
    el.hidden = false;
  }
  throw error;
}
let dbRows: Row[] = [];
let selectedRowIndex: number | null = null;

// Patient-level fields of Block 1 (everything except the two identifiers).
// These stay identical across lesions of one patient, so they can be carried
// over when entering a new lesion for a patient already in the database.
const AUTOFILL_KEY = "medicx-autofill-block1";
const NA_GATE_KEY = "medicx-autona-gate";
const WHATSNEW_KEY = "medicx-whatsnew-seen";
const BLOCK1_PATIENT_FIELDS: string[] = SECTIONS[0].fields
  .map((field) => field.id)
  .filter((id) => id !== "Patient_ID" && id !== "Lesion_ID");

async function refresh(): Promise<void> {
  dbRows = await repo.list();
}

// --- DOM helpers -----------------------------------------------------------

function byId<T extends HTMLElement = HTMLElement>(id: string): T {
  return document.getElementById(id) as T;
}

function fieldEl(id: string): HTMLInputElement | HTMLSelectElement | null {
  return document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null;
}

// --- Form rendering --------------------------------------------------------

function renderSections(): void {
  const host = byId("sections");
  host.innerHTML = SECTIONS.map(
    (section, index) => `
      <section class="section">
        <header class="section-header">
          <div class="section-index">${index + 1}</div>
          <h3 class="section-title">${escapeHtml(section.title)}</h3>
          <span class="section-count">${section.fields.length} полей</span>
        </header>
        <div class="field-grid">
          ${section.fields.map(renderField).join("")}
        </div>
      </section>
    `,
  ).join("");
}

function renderField(field: FieldDef): string {
  const choices = field.type === "select" ? parseChoices(field.hint) : [];
  const control =
    field.type === "select"
      ? `<select id="${field.id}" name="${field.id}" data-field="${field.id}">
          <option value="">—</option>
          ${choices.map((choice) => `<option value="${escapeAttr(choice.value)}">${escapeHtml(choice.label)}</option>`).join("")}
        </select>`
      : `<input id="${field.id}" name="${field.id}" data-field="${field.id}" type="text" ${field.type === "number" ? 'inputmode="decimal"' : ""} ${field.type === "formula" ? "readonly" : ""}>`;

  return `
    <div class="field ${field.type === "formula" ? "formula" : ""}" data-wrap="${field.id}">
      <div class="field-label">
        <label for="${field.id}">${escapeHtml(field.label)}</label>
        <span class="code">${escapeHtml(field.hint)}</span>
        <span class="tech">${field.id}</span>
      </div>
      <div class="field-control">${control}</div>
    </div>
  `;
}

function bindEvents(): void {
  byId("crfForm").addEventListener("input", () => {
    updateComputed();
    saveDraft();
    updateProgress();
  });
  byId("crfForm").addEventListener("change", () => {
    applyGating();
    updateComputed();
    saveDraft();
    updateProgress();
  });
  byId("saveBtn").addEventListener("click", saveCurrent);
  byId("printBtn").addEventListener("click", printCurrent);

  const autofillCb = byId<HTMLInputElement>("autofillBlock1");
  autofillCb.checked = localStorage.getItem(AUTOFILL_KEY) !== "0";
  autofillCb.addEventListener("change", () =>
    localStorage.setItem(AUTOFILL_KEY, autofillCb.checked ? "1" : "0"),
  );
  byId("autofillBtn").addEventListener("click", () => maybeAutofillBlock1(true));
  fieldEl("Patient_ID")?.addEventListener("change", () => maybeAutofillBlock1(false));

  const vesselEl = fieldEl("Vessel");
  vesselEl?.addEventListener("blur", () => {
    const canonical = normalizeVessel(vesselEl.value);
    if (canonical !== vesselEl.value) vesselEl.value = canonical;
  });

  const naGateCb = byId<HTMLInputElement>("autoNaGate");
  naGateCb.checked = localStorage.getItem(NA_GATE_KEY) !== "0";
  naGateCb.addEventListener("change", () => {
    localStorage.setItem(NA_GATE_KEY, naGateCb.checked ? "1" : "0");
    applyGating();
    updateComputed();
    saveDraft();
    updateProgress();
  });
  byId("clearBtn").addEventListener("click", clearForm);
  byId("csvBtn").addEventListener("click", exportCsv);
  byId("cleanCsvBtn").addEventListener("click", exportCleanCsv);
  byId("codebookBtn").addEventListener("click", exportCodebook);
  byId("jsonBtn").addEventListener("click", exportJson);
  byId("importBtn").addEventListener("click", () => byId<HTMLInputElement>("importFile").click());
  byId("importFile").addEventListener("change", importJson);
  byId("searchRows").addEventListener("input", renderPatientList);
  byId("clearDbBtn").addEventListener("click", clearDatabase);
  byId("runStatsBtn").addEventListener("click", runStats);
  byId("statScenario").addEventListener("change", (event) => {
    applyScenario((event.target as HTMLSelectElement).value);
    runStats();
  });
  byId("statMode").addEventListener("change", runStats);
  byId("authForm").addEventListener("submit", onLogin);
  byId("whatsNewBtn").addEventListener("click", openWhatsNew);
  byId("whatsNewClose").addEventListener("click", closeWhatsNew);
  byId("whatsNewBackdrop").addEventListener("click", closeWhatsNew);
  document.addEventListener("keydown", (event) => {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s") {
      event.preventDefault();
      saveCurrent();
    }
    if (event.key === "Escape" && !byId("whatsNewModal").hidden) closeWhatsNew();
  });
}

// --- Stats controls --------------------------------------------------------

function renderStatsControls(): void {
  const label = (id: string) => `${id} — ${getFieldMeta(id)?.label || id}`;
  byId<HTMLSelectElement>("statScenario").innerHTML = ANALYSIS_SCENARIOS.map(
    (scenario) => `<option value="${scenario.id}">${escapeHtml(scenario.title)}</option>`,
  ).join("");
  const groupIds = ANALYSIS_COLUMNS.filter((id) => {
    const field = getFieldMeta(id);
    return field && field.type === "select";
  });
  const outcomeIds = ANALYSIS_COLUMNS.filter((id) => {
    const field = getFieldMeta(id);
    return field && ["number", "formula", "select"].includes(field.type) && !["Patient_ID", "Lesion_ID"].includes(id);
  });
  const covariateIds = ANALYSIS_COLUMNS.filter((id) => {
    const field = getFieldMeta(id);
    return field && ["number", "formula", "select"].includes(field.type) && !id.includes("_FU") && !["Patient_ID", "Lesion_ID"].includes(id);
  });
  byId("statGroup").innerHTML = groupIds.map((id) => `<option value="${id}">${escapeHtml(label(id))}</option>`).join("");
  byId("statOutcome").innerHTML = outcomeIds.map((id) => `<option value="${id}">${escapeHtml(label(id))}</option>`).join("");
  byId("statCovariates").innerHTML = covariateIds.map((id) => `<option value="${id}">${escapeHtml(label(id))}</option>`).join("");
  applyScenario("manual");
}

function applyScenario(id: string): void {
  const scenario = ANALYSIS_SCENARIOS.find((item) => item.id === id) || ANALYSIS_SCENARIOS[0];
  byId<HTMLSelectElement>("statScenario").value = scenario.id;
  byId<HTMLSelectElement>("statMode").value = scenario.mode;
  byId<HTMLSelectElement>("statGroup").value = scenario.group;
  byId<HTMLSelectElement>("statOutcome").value = scenario.outcome;
  [...byId<HTMLSelectElement>("statCovariates").options].forEach((option) => {
    option.selected = scenario.covariates.includes(option.value);
  });
}

// --- Form data <-> DOM -----------------------------------------------------

function getFormData(): Row {
  updateComputed();
  const data: Row = {};
  DB_COLUMNS.forEach((id) => {
    const el = fieldEl(id);
    data[id] = el ? el.value.trim() : "";
  });
  data.Vessel = normalizeVessel(data.Vessel);
  return data;
}

function setFormData(row: Row): void {
  DB_COLUMNS.forEach((id) => {
    const el = fieldEl(id);
    if (el) {
      el.disabled = false; // reset locks before re-evaluating gating for this row
      el.value = row?.[id] ?? "";
    }
  });
  applyGating();
  updateComputed();
  saveDraft();
  updateProgress();
  window.scrollTo({ top: 0, behavior: "smooth" });
}

/**
 * Carry over Block 1 (patient-level clinico-anamnestic data) from an existing
 * lesion of the same patient. Triggered automatically when Patient_ID matches a
 * record already in the database (toggle in the form), or manually via button.
 * Non-destructive: only fills fields that are currently empty, so anything the
 * user already typed is never overwritten.
 */
function maybeAutofillBlock1(manual: boolean): void {
  const enabled = byId<HTMLInputElement>("autofillBlock1").checked;
  if (!manual && !enabled) return;

  const patientId = fieldEl("Patient_ID")?.value.trim() ?? "";
  if (!patientId) {
    if (manual) showToast("Сначала укажите Patient_ID.");
    return;
  }
  const currentLesion = fieldEl("Lesion_ID")?.value.trim() ?? "";

  const matches = dbRows.filter(
    (row) => row.Patient_ID === patientId && row.Lesion_ID !== currentLesion,
  );
  if (!matches.length) {
    if (manual) showToast(`Пациент ${patientId} ещё не встречается в базе.`);
    return;
  }
  // Most recently saved lesion of this patient is the freshest source.
  const source = matches.reduce((latest, row) =>
    (row._saved_at ?? "") > (latest._saved_at ?? "") ? row : latest,
  );

  let filled = 0;
  BLOCK1_PATIENT_FIELDS.forEach((id) => {
    const el = fieldEl(id);
    if (!el || el.value.trim() !== "") return;
    const value = source[id] ?? "";
    if (value === "") return;
    el.value = value;
    filled += 1;
  });

  updateComputed();
  saveDraft();
  updateProgress();
  if (filled) {
    showToast(`Блок 1 заполнен из ${source.Lesion_ID} (полей: ${filled}).`);
  } else if (manual) {
    showToast("Блок 1 уже заполнен — переносить нечего.");
  }
}

/**
 * Auto-code dependent fields as N/A when their gate says the procedure was not
 * performed (e.g. IVUS_Performed = 0 -> IVUS measurements become 99 / -99), and
 * lock them so they can't be edited. Releasing the gate clears the sentinel and
 * unlocks the fields. Pluggable via the "Авто N/A" toggle; when off, everything
 * is unlocked and nothing is auto-coded.
 */
function applyGating(): void {
  const enabled = byId<HTMLInputElement>("autoNaGate").checked;
  GATING_RULES.forEach((rule) => {
    const gateValue = fieldEl(rule.gate)?.value.trim() ?? "";
    const notDone = enabled && rule.notDone.includes(gateValue);
    rule.dependents.forEach((id) => {
      const el = fieldEl(id);
      const meta = getFieldMeta(id);
      if (!el || !meta) return;
      const autoCode = rule.autoCode ?? naCodeFor(meta);
      const wrap = el.closest("[data-wrap]");
      if (notDone) {
        el.value = autoCode;
        el.disabled = true;
        wrap?.classList.add("na-locked");
      } else if (el.disabled) {
        // Was auto-locked: release and clear the auto-coded value for real data entry.
        el.disabled = false;
        wrap?.classList.remove("na-locked");
        if (el.value.trim() === autoCode) el.value = "";
      }
    });
  });
}

async function saveCurrent(): Promise<void> {
  clearInvalidMarks();
  const row = getFormData();
  const missing = ["Patient_ID", "Lesion_ID"].filter((id) => !row[id]);
  if (missing.length) {
    missing.forEach((id) => document.querySelector(`[data-wrap="${id}"]`)?.classList.add("invalid"));
    showToast("Заполните Patient_ID и Lesion_ID перед сохранением.");
    byId(missing[0]).focus();
    return;
  }

  const sameIndex = dbRows.findIndex((existing) => existing.Patient_ID === row.Patient_ID && existing.Lesion_ID === row.Lesion_ID);
  if (sameIndex >= 0 && sameIndex !== selectedRowIndex) {
    const ok = confirm(`Запись ${row.Patient_ID} / ${row.Lesion_ID} уже есть в базе. Заменить ее?`);
    if (!ok) return;
    selectedRowIndex = sameIndex;
  }

  // Carry over backend identity / flags when replacing an existing record.
  const existing = selectedRowIndex != null ? dbRows[selectedRowIndex] : undefined;
  if (existing) {
    if (existing._pb_id) row._pb_id = existing._pb_id;
    if (existing._owner) row._owner = existing._owner;
    if (existing._synthetic) row._synthetic = existing._synthetic;
  }
  row._saved_at = new Date().toISOString();

  try {
    await repo.upsert(row);
    await refresh();
  } catch (error) {
    showToast(`Не удалось сохранить: ${errorMessage(error)}`);
    return;
  }
  selectedRowIndex = dbRows.findIndex((r) => r.Patient_ID === row.Patient_ID && r.Lesion_ID === row.Lesion_ID);
  renderDatabase();
  renderPatientList();
  updateProgress();
  showToast(`Запись ${row.Patient_ID} / ${row.Lesion_ID} сохранена в базу.`);
}

function updateComputed(): void {
  const n = (id: string) => {
    const raw = String(fieldEl(id)?.value ?? "").trim();
    // Quantitative N/A sentinels are missing, so a formula never computes on
    // -99 (e.g. an IVUS ratio for a lesion where IVUS was not performed).
    if (raw === "" || raw === "-99" || raw === "-999") return NaN;
    return Number(raw.replace(",", "."));
  };
  const valid = (value: number) => Number.isFinite(value);
  const set = (id: string, value: number) => {
    const el = fieldEl(id);
    if (!el) return;
    el.value = valid(value) ? round(value) : "";
  };

  const laProx = n("LA_Proximal");
  const laDist = n("LA_Distal");
  const avgRef = valid(laProx) && valid(laDist) ? (laProx + laDist) / 2 : NaN;
  set("LA_Ref_Average", avgRef);
  set("MLA_to_Ref_Ratio", valid(n("MLA_Base")) && valid(avgRef) && avgRef !== 0 ? n("MLA_Base") / avgRef : NaN);
  set("Stent_Length_Total", sumIfAny(n("Stent_Planned_Length"), n("BailOut_Stent_Length")));
  set("MLA_Post_Ratio", valid(n("MLA_Post")) && valid(avgRef) && avgRef !== 0 ? n("MLA_Post") / avgRef : NaN);
  set("LLL_FU", valid(n("MLD_Post")) && valid(n("MLD_FU")) ? n("MLD_Post") - n("MLD_FU") : NaN);
}

function updateProgress(): void {
  const row = getFormData();
  const filled = DB_COLUMNS.filter((id) => row[id] !== "").length;
  byId("filledCount").textContent = String(filled);
  byId("rowCount").textContent = String(dbRows.length);
  const patient = row.Patient_ID || "Patient_ID не указан";
  const lesion = row.Lesion_ID ? ` / ${row.Lesion_ID}` : "";
  byId("currentRecord").textContent = patient + lesion;
}

// --- Database views --------------------------------------------------------

function renderDatabase(): void {
  const table = byId("dbTable");
  const previewColumns = ["Patient_ID", "Lesion_ID", "Age", "Sex", "Vessel", "Strategy_Planned", "MLA_to_Ref_Ratio", "Procedure_Success", "FollowUp_Months"];
  table.innerHTML = `
    <thead>
      <tr>
        <th>#</th>
        ${previewColumns.map((col) => `<th>${col}</th>`).join("")}
        <th>Действия</th>
      </tr>
    </thead>
    <tbody>
      ${dbRows.length
        ? dbRows
            .map(
              (row, index) => `
        <tr>
          <td>${index + 2}</td>
          ${previewColumns.map((col) => `<td class="${row[col] ? "" : "empty"}">${escapeHtml(row[col] || "—")}</td>`).join("")}
          <td>
            <button class="mini-btn" type="button" data-load="${index}">Открыть</button>
            <button class="mini-btn danger" type="button" data-delete="${index}">Удалить</button>
          </td>
        </tr>
      `,
            )
            .join("")
        : `<tr><td class="empty" colspan="${previewColumns.length + 2}">Пока нет сохраненных строк</td></tr>`}
    </tbody>
  `;
  table.querySelectorAll<HTMLElement>("[data-load]").forEach((btn) => {
    btn.addEventListener("click", () => loadRow(Number(btn.dataset.load)));
  });
  table.querySelectorAll<HTMLElement>("[data-delete]").forEach((btn) => {
    btn.addEventListener("click", () => deleteRow(Number(btn.dataset.delete)));
  });
}

function renderPatientList(): void {
  const host = byId("patientList");
  const query = byId<HTMLInputElement>("searchRows").value.trim().toLowerCase();
  const rows = dbRows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => {
      const hay = `${row.Patient_ID || ""} ${row.Lesion_ID || ""} ${row.Vessel || ""}`.toLowerCase();
      return !query || hay.includes(query);
    });

  host.innerHTML = rows.length
    ? rows
        .map(
          ({ row, index }) => `
      <button class="patient-row ${index === selectedRowIndex ? "active" : ""}" type="button" data-row="${index}">
        <b>${escapeHtml(row.Patient_ID || "—")} / ${escapeHtml(row.Lesion_ID || "—")}${row._synthetic ? " · SYN" : ""}</b>
        <span>${escapeHtml(row.Vessel || "сосуд не указан")} · ${escapeHtml(strategyLabel(row.Strategy_Planned) || "стратегия не указана")}</span>
      </button>
    `,
        )
        .join("")
    : `<div class="patient-row"><b>Нет записей</b><span>Сохраните первую ИРК</span></div>`;

  host.querySelectorAll<HTMLElement>("[data-row]").forEach((btn) => {
    btn.addEventListener("click", () => loadRow(Number(btn.dataset.row)));
  });
}

function loadRow(index: number): void {
  if (!dbRows[index]) return;
  selectedRowIndex = index;
  setFormData(dbRows[index]);
  renderPatientList();
  showToast("Запись загружена в форму.");
}

async function deleteRow(index: number): Promise<void> {
  if (!dbRows[index]) return;
  const row = dbRows[index];
  if (!confirm(`Удалить строку ${row.Patient_ID || ""} / ${row.Lesion_ID || ""}?`)) return;
  try {
    await repo.remove(row);
    await refresh();
  } catch (error) {
    showToast(`Не удалось удалить: ${errorMessage(error)}`);
    return;
  }
  selectedRowIndex = null;
  renderDatabase();
  renderPatientList();
  updateProgress();
  showToast("Строка удалена.");
}

function clearForm(): void {
  if (!confirm("Очистить текущую форму? Сохраненная база не изменится.")) return;
  selectedRowIndex = null;
  byId<HTMLFormElement>("crfForm").reset();
  clearInvalidMarks();
  applyGating(); // release any locks left by the previous record
  updateComputed();
  saveDraft();
  updateProgress();
  renderPatientList();
  showToast("Форма очищена.");
}

async function clearDatabase(): Promise<void> {
  if (!confirm("Полностью очистить общую базу (будут удалены только ваши записи и те, на которые у вас есть права)?")) return;
  let removed = 0;
  let kept = 0;
  for (const row of [...dbRows]) {
    try {
      await repo.remove(row);
      removed++;
    } catch {
      kept++;
    }
  }
  await refresh();
  selectedRowIndex = null;
  renderDatabase();
  renderPatientList();
  updateProgress();
  showToast(kept ? `Удалено строк: ${removed}; без прав на удаление: ${kept}.` : "База очищена.");
}

// --- Print -----------------------------------------------------------------

function printCurrent(): void {
  updatePrintCard(getFormData());
  window.print();
}

function updatePrintCard(row: Row): void {
  const host = byId("printCard");
  host.innerHTML = `
    <div class="print-title">
      <h1>Индивидуальная регистрационная карта</h1>
      <div class="print-meta">
        <div><b>Patient_ID:</b> ${escapeHtml(row.Patient_ID || "—")}</div>
        <div><b>Lesion_ID:</b> ${escapeHtml(row.Lesion_ID || "—")}</div>
        <div><b>Дата печати:</b> ${new Date().toLocaleDateString("ru-RU")}</div>
      </div>
    </div>
    ${SECTIONS.map(
      (section) => `
      <section class="print-section">
        <h2>${escapeHtml(section.title)}</h2>
        <div class="print-grid">
          ${section.fields
            .map(
              (field) => `
            <div class="print-row">
              <b>${escapeHtml(field.label)}</b>
              <span>${escapeHtml(row[field.id] || "—")}</span>
            </div>
          `,
            )
            .join("")}
        </div>
      </section>
    `,
    ).join("")}
  `;
}

// --- Statistics rendering --------------------------------------------------

function runStats(): void {
  const scenario = ANALYSIS_SCENARIOS.find((item) => item.id === byId<HTMLSelectElement>("statScenario").value) || ANALYSIS_SCENARIOS[0];
  const mode = byId<HTMLSelectElement>("statMode").value;
  const groupId = byId<HTMLSelectElement>("statGroup").value;
  const outcomeId = byId<HTMLSelectElement>("statOutcome").value;
  const covariates = [...byId<HTMLSelectElement>("statCovariates").selectedOptions]
    .map((option) => option.value)
    .filter((id) => id !== groupId && id !== outcomeId);
  // Blank N/A sentinels (99 / -99) so no calculation ever averages them in.
  const allRows = dbRows
    .filter((row) => row && row.Patient_ID && row.Lesion_ID)
    .map(sanitizeForAnalysis);
  const rows = scenario.filter ? allRows.filter((row) => scenario.filter!(row)) : allRows;
  const output = byId("statsOutput");

  if (rows.length < 2) {
    output.innerHTML = `<div class="stats-empty">Для анализа нужно минимум 2 сохраненные строки. Сейчас в выбранной подвыборке: ${rows.length}.</div>`;
    return;
  }

  try {
    const recommendation = buildMethodRecommendation(rows, groupId, outcomeId, covariates);
    const blocks = [renderScenarioNote(scenario, allRows.length, rows.length), renderRecommendation(recommendation)];
    if (mode === "auto") {
      blocks.push(renderDescriptiveStats(rows, outcomeId));
      if (recommendation.groups.length > 2 && scenario.reference) {
        blocks.push(safeStatsBlock(() => renderPairwiseComparisons(rows, groupId, outcomeId, scenario.reference!), "Pairwise vs reference"));
      }
      if (recommendation.primary === "ttest") blocks.push(safeStatsBlock(() => renderTTest(rows, groupId, outcomeId), "t-test"));
      if (recommendation.primary === "fisher") blocks.push(safeStatsBlock(() => renderFisher(rows, groupId, outcomeId), "Fisher"));
      if (covariates.length && recommendation.canAncova) blocks.push(safeStatsBlock(() => renderAncova(rows, groupId, outcomeId, covariates), "ANCOVA"));
      if (covariates.length && recommendation.canPsm) blocks.push(safeStatsBlock(() => renderPsm(rows, groupId, outcomeId, covariates), "PSM"));
    } else if (mode === "descriptive") {
      blocks.push(renderDescriptiveStats(rows, outcomeId, true));
    } else if (mode === "ttest") {
      blocks.push(renderTTest(rows, groupId, outcomeId));
    } else if (mode === "fisher") {
      blocks.push(renderFisher(rows, groupId, outcomeId));
    } else if (mode === "ancova") {
      blocks.push(renderAncova(rows, groupId, outcomeId, covariates));
    } else if (mode === "psm") {
      blocks.push(renderPsm(rows, groupId, outcomeId, covariates));
    }
    output.innerHTML = blocks.join("");
  } catch (error) {
    output.innerHTML = `<div class="stats-empty">Не удалось рассчитать анализ: ${escapeHtml(errorMessage(error))}</div>`;
  }
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

function renderScenarioNote(scenario: Scenario, totalRows: number, usedRows: number): string {
  if (scenario.id === "manual" && !scenario.filterLabel) return "";
  return `
    <div class="method-note">
      <b>Сценарий: ${escapeHtml(scenario.title)}.</b>
      ${scenario.filterLabel ? `Подвыборка: ${escapeHtml(scenario.filterLabel)}; строк ${usedRows} из ${totalRows}.` : `Строк: ${usedRows}.`}
      Основной фокус исследования — non-inferiority исследуемых методик относительно FMJ; p-value из браузера не заменяет заранее заданную границу non-inferiority и финальный расчет в статистическом пакете.
    </div>
  `;
}

function safeStatsBlock(render: () => string, label: string): string {
  try {
    return render();
  } catch (error) {
    return `<div class="stats-empty">${escapeHtml(label)} не рассчитан: ${escapeHtml(errorMessage(error))}</div>`;
  }
}

function renderRecommendation(info: MethodRecommendation): string {
  const methodName =
    {
      descriptive: "Описательная статистика",
      ttest: "Welch / Student t-test",
      fisher: "Критерий Фишера",
      ancova: "ANCOVA",
      psm: "Propensity Score Matching",
    }[info.primary] || "Описательная статистика";
  return `
    <div class="method-note">
      <b>Авто-рекомендация: ${escapeHtml(methodName)}.</b>
      Рабочих строк: ${info.usableRows} из ${info.rows}; тип исхода: ${escapeHtml(info.outcomeType)}.
      <div class="pill-row" style="margin-top:8px">
        ${info.groupCounts.map((item) => `<span class="pill">${escapeHtml(item.group)}: n=${item.n}</span>`).join("")}
      </div>
      ${info.notes.length ? `<ul>${info.notes.map((note) => `<li>${escapeHtml(note)}</li>`).join("")}</ul>` : ""}
    </div>
  `;
}

function renderDescriptiveStats(rows: Row[], outcomeId: string, broad = false): string {
  const ids = broad
    ? DB_COLUMNS.filter((id) => ["number", "formula"].includes(FIELD_MAP.get(id)?.type ?? ""))
    : [outcomeId, "Age", "BMI", "MLA_Base", "MLA_Post", "MLA_to_Ref_Ratio", "MLA_Post_Ratio", "LLL_FU"].filter((id, idx, arr) => id && arr.indexOf(id) === idx);
  const stats = ids
    .map((id) => {
      const values = rows.map((row) => num(rowValue(row, id))).filter(Number.isFinite);
      return { id, label: getFieldMeta(id)?.label || id, ...summary(values), missing: rows.length - values.length };
    })
    .filter((item) => item.n > 0);
  return `
    <div class="stats-table">
      <table>
        <thead><tr><th>Параметр</th><th>n</th><th>пропуски</th><th>mean</th><th>SD</th><th>median</th><th>Q1-Q3</th><th>min-max</th></tr></thead>
        <tbody>
          ${stats
            .map(
              (item) => `
            <tr>
              <td><b>${escapeHtml(item.label)}</b><br><span class="tech">${item.id}</span></td>
              <td>${item.n}</td>
              <td>${item.missing}</td>
              <td>${fmt(item.mean)}</td>
              <td>${fmt(item.sd)}</td>
              <td>${fmt(item.median)}</td>
              <td>${fmt(item.q1)} - ${fmt(item.q3)}</td>
              <td>${fmt(item.min)} - ${fmt(item.max)}</td>
            </tr>
          `,
            )
            .join("")}
        </tbody>
      </table>
    </div>
  `;
}

function renderTTest(rows: Row[], groupId: string, outcomeId: string): string {
  const data = rows.filter((row) => hasValue(rowValue(row, groupId)) && Number.isFinite(num(rowValue(row, outcomeId))));
  const groups = uniqueValues(data.map((row) => rowValue(row, groupId)));
  if (groups.length !== 2) return `<div class="stats-empty">t-test требует ровно 2 группы; найдено: ${groups.length}.</div>`;
  const a = data.filter((row) => rowValue(row, groupId) === groups[0]).map((row) => num(rowValue(row, outcomeId)));
  const b = data.filter((row) => rowValue(row, groupId) === groups[1]).map((row) => num(rowValue(row, outcomeId)));
  if (a.length < 2 || b.length < 2) return `<div class="stats-empty">Для t-test нужно минимум 2 числовых значения в каждой группе.</div>`;
  const sa = summary(a);
  const sb = summary(b);
  const diff = sb.mean - sa.mean;
  const se = Math.sqrt(sa.variance / sa.n + sb.variance / sb.n);
  const t = diff / se;
  const df =
    Math.pow(sa.variance / sa.n + sb.variance / sb.n, 2) /
    (Math.pow(sa.variance / sa.n, 2) / (sa.n - 1) + Math.pow(sb.variance / sb.n, 2) / (sb.n - 1));
  const p = 2 * (1 - studentTCdf(Math.abs(t), df));
  return `
    <div class="result-grid">
      <div class="result-card"><h4>${escapeHtml(groups[0])}</h4><strong>${fmt(sa.mean)}</strong><span>n=${sa.n}, SD=${fmt(sa.sd)}</span></div>
      <div class="result-card"><h4>${escapeHtml(groups[1])}</h4><strong>${fmt(sb.mean)}</strong><span>n=${sb.n}, SD=${fmt(sb.sd)}</span></div>
      <div class="result-card"><h4>Welch t-test</h4><strong>p=${fmtP(p)}</strong><span>Δ=${fmt(diff)}, t=${fmt(t)}, df=${fmt(df)}</span></div>
    </div>
    <div class="method-note">Разность считается как ${escapeHtml(groups[1])} минус ${escapeHtml(groups[0])}. При выраженной асимметрии или очень малой группе экспортируйте CSV и дополнительно проверьте Mann-Whitney/bootstrapping.</div>
  `;
}

function renderPairwiseComparisons(rows: Row[], groupId: string, outcomeId: string, reference: string): string {
  const data = rows.filter((row) => hasValue(rowValue(row, groupId)) && hasValue(rowValue(row, outcomeId)));
  const groups = uniqueValues(data.map((row) => rowValue(row, groupId)));
  const ref = groups.includes(reference) ? reference : groups.find((group) => strategyLabel(group) === reference) || reference;
  if (!groups.includes(ref)) {
    return `<div class="stats-empty">Reference-группа ${escapeHtml(reference)} не найдена в данных.</div>`;
  }
  const outcomeType = inferVariableType(data, outcomeId);
  const comparisons = groups
    .filter((group) => group !== ref)
    .map((group) => {
      const subset = data.filter((row) => [ref, group].includes(rowValue(row, groupId)));
      if (outcomeType === "numeric") return pairwiseT(subset, groupId, outcomeId, ref, group);
      if (outcomeType === "binary") return pairwiseFisher(subset, groupId, outcomeId, ref, group);
      return { group, note: "исход не бинарный и не количественный" };
    });
  return `
    <div class="stats-table">
      <table>
        <thead><tr><th>Сравнение</th><th>n ref</th><th>n test</th><th>эффект</th><th>p</th><th>метод</th></tr></thead>
        <tbody>
          ${comparisons
            .map(
              (item) => `
            <tr>
              <td>${escapeHtml(item.group)} vs ${escapeHtml(ref)}</td>
              <td>${item.nRef ?? "—"}</td>
              <td>${item.nTest ?? "—"}</td>
              <td>${fmt(item.effect ?? NaN)}</td>
              <td>${fmtP(item.p ?? NaN)}</td>
              <td>${escapeHtml(item.method || item.note || "—")}</td>
            </tr>
          `,
            )
            .join("")}
        </tbody>
      </table>
    </div>
    <div class="method-note">Парные сравнения сделаны относительно reference=${escapeHtml(ref)}. Для настоящего non-inferiority здесь нужно отдельно задать клиническую границу нехужественности и доверительный интервал разности, а не только p-value.</div>
  `;
}

function renderFisher(rows: Row[], groupId: string, outcomeId: string): string {
  const data = rows.filter((row) => hasValue(rowValue(row, groupId)) && hasValue(rowValue(row, outcomeId)));
  const groups = uniqueValues(data.map((row) => rowValue(row, groupId)));
  const outcomes = uniqueValues(data.map((row) => rowValue(row, outcomeId)));
  if (groups.length !== 2 || outcomes.length !== 2) {
    return `<div class="stats-empty">Критерий Фишера требует таблицу 2x2. Групп: ${groups.length}, уровней исхода: ${outcomes.length}.</div>`;
  }
  const table2 = [
    [
      data.filter((row) => rowValue(row, groupId) === groups[0] && rowValue(row, outcomeId) === outcomes[0]).length,
      data.filter((row) => rowValue(row, groupId) === groups[0] && rowValue(row, outcomeId) === outcomes[1]).length,
    ],
    [
      data.filter((row) => rowValue(row, groupId) === groups[1] && rowValue(row, outcomeId) === outcomes[0]).length,
      data.filter((row) => rowValue(row, groupId) === groups[1] && rowValue(row, outcomeId) === outcomes[1]).length,
    ],
  ];
  const p = fisherExact2x2(table2);
  const risk0 = table2[0][1] / (table2[0][0] + table2[0][1]);
  const risk1 = table2[1][1] / (table2[1][0] + table2[1][1]);
  return `
    <div class="result-grid">
      <div class="result-card"><h4>${escapeHtml(groups[0])}</h4><strong>${table2[0][1]}/${table2[0][0] + table2[0][1]}</strong><span>${escapeHtml(outcomes[1])}, риск=${fmt(risk0)}</span></div>
      <div class="result-card"><h4>${escapeHtml(groups[1])}</h4><strong>${table2[1][1]}/${table2[1][0] + table2[1][1]}</strong><span>${escapeHtml(outcomes[1])}, риск=${fmt(risk1)}</span></div>
      <div class="result-card"><h4>Fisher exact</h4><strong>p=${fmtP(p)}</strong><span>Risk difference=${fmt(risk1 - risk0)}</span></div>
    </div>
    <div class="stats-table">
      <table><thead><tr><th></th><th>${escapeHtml(outcomes[0])}</th><th>${escapeHtml(outcomes[1])}</th></tr></thead>
      <tbody>
        <tr><td>${escapeHtml(groups[0])}</td><td>${table2[0][0]}</td><td>${table2[0][1]}</td></tr>
        <tr><td>${escapeHtml(groups[1])}</td><td>${table2[1][0]}</td><td>${table2[1][1]}</td></tr>
      </tbody></table>
    </div>
  `;
}

function renderAncova(rows: Row[], groupId: string, outcomeId: string, covariates: string[]): string {
  if (!covariates.length) return `<div class="stats-empty">Для ANCOVA выберите хотя бы одну ковариату.</div>`;
  const base = rows.filter((row) => hasValue(rowValue(row, groupId)) && Number.isFinite(num(rowValue(row, outcomeId))));
  const groups = uniqueValues(base.map((row) => rowValue(row, groupId)));
  if (groups.length !== 2) return `<div class="stats-empty">ANCOVA в этом модуле требует бинарную группу.</div>`;
  const dataset = base.filter((row) => covariates.every((id) => hasValue(rowValue(row, id))));
  const y = dataset.map((row) => num(rowValue(row, outcomeId)));
  if (dataset.length < covariates.length + 4) return `<div class="stats-empty">Слишком мало полных строк для модели: ${dataset.length}. Уменьшите число ковариат.</div>`;
  const design = buildDesignMatrix(dataset, covariates);
  const treatment: number[] = dataset.map((row) => (rowValue(row, groupId) === groups[1] ? 1 : 0));
  const X = treatment.map((t, i) => [1, t, ...design.X[i]]);
  const names = ["Intercept", `${groupId}: ${groups[1]} vs ${groups[0]}`, ...design.names];
  const fit = ols(X, y);
  const beta = fit.beta[1];
  const se = fit.se[1];
  const t = beta / se;
  const p = 2 * (1 - studentTCdf(Math.abs(t), fit.df));
  return `
    <div class="result-grid">
      <div class="result-card"><h4>ANCOVA effect</h4><strong>${fmt(beta)}</strong><span>${escapeHtml(groups[1])} vs ${escapeHtml(groups[0])}</span></div>
      <div class="result-card"><h4>p-value</h4><strong>p=${fmtP(p)}</strong><span>SE=${fmt(se)}, t=${fmt(t)}, df=${fit.df}</span></div>
      <div class="result-card"><h4>Модель</h4><strong>R²=${fmt(fit.r2)}</strong><span>n=${dataset.length}, ковариат: ${design.names.length}</span></div>
    </div>
    <div class="stats-table">
      <table><thead><tr><th>Коэффициент</th><th>beta</th><th>SE</th><th>t</th><th>p</th></tr></thead>
        <tbody>${names
          .map(
            (name, i) =>
              `<tr><td>${escapeHtml(name)}</td><td>${fmt(fit.beta[i])}</td><td>${fmt(fit.se[i])}</td><td>${fmt(fit.beta[i] / fit.se[i])}</td><td>${fmtP(2 * (1 - studentTCdf(Math.abs(fit.beta[i] / fit.se[i]), fit.df)))}</td></tr>`,
          )
          .join("")}</tbody>
      </table>
    </div>
  `;
}

function renderPsm(rows: Row[], groupId: string, outcomeId: string, covariates: string[]): string {
  if (!covariates.length) return `<div class="stats-empty">Для PSM выберите ковариаты, по которым нужно балансировать группы.</div>`;
  const base = rows.filter((row) => hasValue(rowValue(row, groupId)) && hasValue(rowValue(row, outcomeId)) && covariates.every((id) => hasValue(rowValue(row, id))));
  const groups = uniqueValues(base.map((row) => rowValue(row, groupId)));
  if (groups.length !== 2) return `<div class="stats-empty">PSM требует бинарную экспозицию; найдено уровней: ${groups.length}.</div>`;
  if (base.length < 6) return `<div class="stats-empty">Для PSM слишком мало полных строк: ${base.length}.</div>`;
  const design = buildDesignMatrix(base, covariates);
  if (!design.names.length) return `<div class="stats-empty">Не получилось построить ковариаты для propensity score.</div>`;
  const treatment: number[] = base.map((row) => (rowValue(row, groupId) === groups[1] ? 1 : 0));
  if (treatment.reduce((sum, value) => sum + value, 0) < 2 || treatment.filter((v) => v === 0).length < 2) {
    return `<div class="stats-empty">Для PSM нужно минимум по 2 наблюдения в каждой группе.</div>`;
  }
  const ps = logisticScores(design.X, treatment);
  const logits = ps.map((p) => Math.log(p / (1 - p)));
  const caliperFactor = Number(String(byId<HTMLInputElement>("statCaliper").value || "0.2").replace(",", ".")) || 0.2;
  const caliper = caliperFactor * summary(logits).sd;
  const matches = nearestMatches(base, treatment, logits, caliper);
  if (!matches.length) return `<div class="stats-empty">Не найдено пар в пределах caliper=${fmt(caliper)}. Попробуйте увеличить caliper или уменьшить набор ковариат.</div>`;
  const before = maxAbsSmd(design.X, treatment);
  const matchedIndexes = matches.flatMap((pair) => [pair.treated, pair.control]);
  const matchedX = matchedIndexes.map((i) => design.X[i]);
  const matchedT = matchedIndexes.map((i) => treatment[i]);
  const after = maxAbsSmd(matchedX, matchedT);
  const effect = psmOutcomeEffect(base, matches, outcomeId);
  return `
    <div class="result-grid">
      <div class="result-card"><h4>Matched pairs</h4><strong>${matches.length}</strong><span>treated=${treatment.reduce((s, v) => s + v, 0)}, control=${treatment.filter((v) => v === 0).length}</span></div>
      <div class="result-card"><h4>ATT</h4><strong>${fmt(effect.value)}</strong><span>${escapeHtml(effect.label)}</span></div>
      <div class="result-card"><h4>Balance</h4><strong>${fmt(before)} → ${fmt(after)}</strong><span>max |SMD| до и после matching</span></div>
    </div>
    <div class="method-note">PSM: treated = ${escapeHtml(groups[1])}, control = ${escapeHtml(groups[0])}; логистическая модель propensity score, nearest-neighbor matching без замены, caliper=${fmt(caliper)} (${fmt(caliperFactor)} SD logit PS). Это скрининг баланса, не финальный протокол исследования.</div>
  `;
}

// --- Export / import -------------------------------------------------------

function exportCsv(): void {
  download(`medicx_database_${dateStamp()}.csv`, rowsToCsv(dbRows), "text/csv;charset=utf-8");
  showToast("CSV экспортирован.");
}

function exportCleanCsv(): void {
  const csv = rowsToCleanCsv(dbRows);
  download(`medicx_clean_dataset_${dateStamp()}.csv`, csv, "text/csv;charset=utf-8");
  const real = csv.split("\n").length - 1;
  showToast(`Clean dataset экспортирован (реальных строк: ${real}; синтетика исключена).`);
}

function exportCodebook(): void {
  download(`medicx_codebook_${dateStamp()}.csv`, codebookToCsv(), "text/csv;charset=utf-8");
  showToast("Codebook экспортирован.");
}

function exportJson(): void {
  download(`medicx_database_${dateStamp()}.json`, rowsToJsonPayload(dbRows), "application/json");
  showToast("JSON экспортирован.");
}

function importJson(event: Event): void {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = async () => {
    try {
      const incoming = parseImportedRows(String(reader.result));
      await repo.bulkUpsert(incoming);
      await refresh();
      renderDatabase();
      renderPatientList();
      updateProgress();
      showToast(`Импортировано строк: ${incoming.length}.`);
    } catch {
      showToast("Не удалось импортировать JSON.");
    } finally {
      input.value = "";
    }
  };
  reader.readAsText(file);
}

// --- Draft (in-progress form) ---------------------------------------------

function saveDraft(): void {
  const draft: Record<string, string> = {};
  DB_COLUMNS.forEach((id) => {
    const el = fieldEl(id);
    if (el) draft[id] = el.value;
  });
  localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
}

function restoreDraft(): void {
  try {
    const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}");
    DB_COLUMNS.forEach((id) => {
      const el = fieldEl(id);
      if (el && draft[id] != null) el.value = draft[id];
    });
  } catch {
    localStorage.removeItem(DRAFT_KEY);
  }
}

// --- Misc UI ---------------------------------------------------------------

function download(filename: string, content: string, type: string): void {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function clearInvalidMarks(): void {
  document.querySelectorAll(".field.invalid").forEach((el) => el.classList.remove("invalid"));
}

let toastTimer: ReturnType<typeof setTimeout> | undefined;
function showToast(message: string): void {
  const toast = byId("toast");
  toast.textContent = message;
  toast.classList.add("show");
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove("show"), 2600);
}

// --- Session / auth --------------------------------------------------------

function renderAll(): void {
  renderDatabase();
  renderPatientList();
  updateProgress();
}

function renderSessionBar(): void {
  const bar = byId("sessionBar");
  const user = repo.currentUser();
  if (!user) {
    bar.innerHTML = `<span class="session-mode">Требуется вход</span>`;
    return;
  }
  const roleLabel = user.role === "coordinator" ? "координатор" : "врач";
  bar.innerHTML = `
    <span class="session-user">${escapeHtml(user.email)}</span>
    <span class="session-role">${escapeHtml(roleLabel)}</span>
    <button class="session-logout" id="logoutBtn" type="button">Выйти</button>
  `;
  document.getElementById("logoutBtn")?.addEventListener("click", onLogout);
}

function showAuthGate(show: boolean): void {
  byId("authGate").classList.toggle("show", show);
}

async function loadAndRender(): Promise<void> {
  try {
    await refresh();
  } catch (error) {
    showToast(`Не удалось загрузить базу: ${errorMessage(error)}`);
    dbRows = [];
  }
  showAuthGate(false);
  renderSessionBar();
  renderAll();
}

async function onLogin(event: Event): Promise<void> {
  event.preventDefault();
  const email = byId<HTMLInputElement>("authEmail").value.trim();
  const password = byId<HTMLInputElement>("authPassword").value;
  const err = byId("authError");
  err.textContent = "";
  try {
    await repo.login(email, password);
  } catch (error) {
    err.textContent = `Не удалось войти: ${errorMessage(error)}`;
    return;
  }
  byId<HTMLInputElement>("authPassword").value = "";
  await loadAndRender();
}

function onLogout(): void {
  repo.logout();
  dbRows = [];
  selectedRowIndex = null;
  renderSessionBar();
  renderAll();
  showAuthGate(true);
}

// --- Theme -----------------------------------------------------------------

const THEME_KEY = "medicx-theme";

function applyTheme(dark: boolean): void {
  document.documentElement.dataset.theme = dark ? "dark" : "light";
  localStorage.setItem(THEME_KEY, dark ? "dark" : "");
  const icon = dark ? "☀" : "☾";
  const t1 = document.getElementById("themeToggle");
  const t2 = document.getElementById("themeToggleMob");
  if (t1) t1.textContent = icon;
  if (t2) t2.textContent = icon;
}

// --- What's new ------------------------------------------------------------

function renderWhatsNew(): void {
  byId("whatsNewBody").innerHTML = CHANGELOG.map(
    (release, index) => `
      <div class="wn-release${index === 0 ? " wn-latest" : ""}">
        <div class="wn-release-head">
          <span class="wn-ver">v${escapeHtml(release.version)}</span>
          <span class="wn-date">${escapeHtml(release.date)}</span>
        </div>
        <h3>${escapeHtml(release.title)}</h3>
        <ul>${release.items.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</ul>
      </div>
    `,
  ).join("");
}

function updateWhatsNewBadge(): void {
  byId("whatsNewBadge").hidden = localStorage.getItem(WHATSNEW_KEY) === CURRENT_VERSION;
}

function openWhatsNew(): void {
  byId("whatsNewModal").hidden = false;
  localStorage.setItem(WHATSNEW_KEY, CURRENT_VERSION);
  updateWhatsNewBadge();
  byId("whatsNewClose").focus();
}

function closeWhatsNew(): void {
  byId("whatsNewModal").hidden = true;
}

// --- Init ------------------------------------------------------------------

void (async () => {
  // Apply saved theme before first render to prevent flash
  applyTheme(localStorage.getItem(THEME_KEY) === "dark");

  renderSections();
  renderStatsControls();
  bindEvents();
  restoreDraft();
  applyGating();
  updateComputed();
  renderSessionBar();
  renderWhatsNew();
  updateWhatsNewBadge();

  const toggleTheme = (): void =>
    applyTheme(document.documentElement.dataset.theme !== "dark");
  document.getElementById("themeToggle")?.addEventListener("click", toggleTheme);
  document.getElementById("themeToggleMob")?.addEventListener("click", toggleTheme);

  // Mobile rail drawer
  const railEl = document.querySelector<HTMLElement>(".rail");
  const overlayEl = document.getElementById("railOverlay");
  const hamburgerEl = document.getElementById("hamburger");
  const openRail = (): void => {
    railEl?.classList.add("open");
    overlayEl?.classList.add("show");
    hamburgerEl?.classList.add("is-open");
  };
  const closeRailMob = (): void => {
    railEl?.classList.remove("open");
    overlayEl?.classList.remove("show");
    hamburgerEl?.classList.remove("is-open");
  };
  hamburgerEl?.addEventListener("click", () =>
    railEl?.classList.contains("open") ? closeRailMob() : openRail());
  overlayEl?.addEventListener("click", closeRailMob);

  if (!repo.currentUser()) {
    showAuthGate(true);
    renderAll();
    return;
  }
  await loadAndRender();
})();
